import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";
import {
    formatDate,
    formatPhoneNumber,
    TelecomPhoneLineId,
    TenantCredentialId,
    UserId,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SupportEmailLink } from "components/app/support-email-link";
import { ContentFull, ContentHeader } from "components/layout";
import { linkStyle } from "components/reports/show/custom-reports/custom-reports-sidebar";
import { trpc } from "components/rpc/trpc";
import { CopyableLink } from "components/shared/copyable-link";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { PremiumCallout } from "components/ui/premium-callout";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React, { useMemo } from "react";
import { useProductName } from "store/selectors/branding";
import { useFeature } from "store/selectors/hooks";

interface ManageIntegrationsTwilioProps {}

type TwilioPhoneNumber = {
    sid: string;
    phoneNumber: string;
    assignedUserId: UserId | null;
    phoneLineId: TelecomPhoneLineId | null;
};

const StatusIndicator: React.FC<{ isComplete: boolean; label: string }> = ({
    isComplete,
    label,
}) =>
    isComplete ? (
        <div className="flex items-center text-green-600">
            <CheckCircleIcon className="w-5 h-5 mr-1" />
            <span className="text-sm font-medium">{label}</span>
        </div>
    ) : (
        <div className="flex items-center text-amber-600">
            <ExclamationCircleIcon className="w-5 h-5 mr-1" />
            <span className="text-sm font-medium">Incomplete</span>
        </div>
    );

export const ManageIntegrationsTwilio: React.FC<ManageIntegrationsTwilioProps> = () => {
    const isFeatureEnabled = useFeature("twilioIntegration");
    const productName = useProductName();
    const trpcClient = trpc.useTRPC();

    const [accountSidInput, setAccountSidInput] = React.useState("");
    const [apiKeySidInput, setApiKeySidInput] = React.useState("");
    const [apiKeySecretInput, setApiKeySecretInput] = React.useState("");
    const [voiceAppSidInput, setVoiceAppSidInput] = React.useState("");

    const [showAccountSidForm, setShowAccountSidForm] = React.useState(false);
    const [showApiKeyForm, setShowApiKeyForm] = React.useState(false);
    const [showTwimlAppForm, setShowTwimlAppForm] = React.useState(false);

    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const credentialQuery = useQuery(trpcClient.twilio.getCredential.queryOptions());

    const phoneNumbersQuery = useQuery({
        ...trpcClient.twilio.listActiveNumbers.queryOptions(),
        enabled: credentialQuery.data?.isComplete ?? false,
    });

    const phoneNumbers = useMemo(
        () =>
            (phoneNumbersQuery.data ?? []).map(item => ({
                sid: item.sid,
                phoneNumber: item.phoneNumber ?? "",
                assignedUserId: item.assignedUserId ?? null,
                phoneLineId: item.phoneLineId ?? null,
            })),
        [phoneNumbersQuery.data],
    );

    const storeCredentialMutation = useMutation({
        ...trpcClient.twilio.storeCredential.mutationOptions(),
        onSuccess: () => {
            setErrorMessage(null);
            setSuccessMessage("Credentials saved successfully");
            setShowAccountSidForm(false);
            setShowApiKeyForm(false);
            setShowTwimlAppForm(false);
            setAccountSidInput("");
            setApiKeySidInput("");
            setApiKeySecretInput("");
            setVoiceAppSidInput("");
            credentialQuery.refetch();
        },
        onError: error => {
            setSuccessMessage(null);
            setErrorMessage(error.message || "Failed to save credentials");
        },
    });

    const deleteCredentialMutation = useMutation({
        ...trpcClient.integrations.deleteCredential.mutationOptions(),
        onSuccess: () => {
            setSuccessMessage("Twilio disconnected");
            credentialQuery.refetch();
        },
    });

    const [assignmentError, setAssignmentError] = React.useState<string | null>(null);

    const assignPhoneNumberMutation = useMutation({
        ...trpcClient.twilio.assignPhoneNumber.mutationOptions(),
        onSuccess: () => {
            setAssignmentError(null);
            phoneNumbersQuery.refetch();
        },
        onError: error => {
            setAssignmentError(error.message || "Failed to assign phone number");
        },
    });

    const unassignPhoneNumberMutation = useMutation({
        ...trpcClient.twilio.unassignPhoneNumber.mutationOptions(),
        onSuccess: () => {
            setAssignmentError(null);
            phoneNumbersQuery.refetch();
        },
        onError: error => {
            setAssignmentError(error.message || "Failed to unassign phone number");
        },
    });

    const handleAssignmentChange = React.useCallback(
        (record: TwilioPhoneNumber, newUserId: SelectableUser) => {
            setAssignmentError(null);
            if (newUserId) {
                assignPhoneNumberMutation.mutate({
                    sid: record.sid,
                    phoneNumber: record.phoneNumber,
                    userId: newUserId as UserId,
                });
            } else if (record.phoneLineId) {
                unassignPhoneNumberMutation.mutate({
                    phoneLineId: record.phoneLineId,
                });
            }
        },
        [assignPhoneNumberMutation, unassignPhoneNumberMutation],
    );

    const isAssignmentPending =
        assignPhoneNumberMutation.isPending || unassignPhoneNumberMutation.isPending;

    const phoneNumberColumns: CrmTableProps<TwilioPhoneNumber>["columns"] = useMemo(
        () => [
            {
                key: "phoneNumber",
                header: "Phone Number",
                renderCell: ({ record }) => <>{formatPhoneNumber(record.phoneNumber)}</>,
            },
            {
                key: "assignedUserId" as keyof TwilioPhoneNumber,
                header: "Assigned User",
                renderCell: ({ record }) => (
                    <UserSelect
                        label=""
                        value={record.assignedUserId ?? undefined}
                        onChange={newUserId => handleAssignmentChange(record, newUserId)}
                        includeEveryone={false}
                        includeUnassigned={true}
                        unassignedLabel="Not Assigned"
                        disabled={isAssignmentPending}
                    />
                ),
            },
        ],
        [handleAssignmentChange, isAssignmentPending],
    );

    const handleDisconnect = React.useCallback(
        async (id: TenantCredentialId) => {
            const confirmed = await fancyConfirm(
                "Disconnect Twilio?",
                "This will remove your Twilio credentials. You will need to reconnect to use SMS and voice features.",
                "Yes, disconnect",
                "Cancel",
            );

            if (confirmed) {
                deleteCredentialMutation.mutate({ id });
            }
        },
        [deleteCredentialMutation],
    );

    const handleSaveAccountSid = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
        storeCredentialMutation.mutate({
            accountSid: accountSidInput,
        });
    }, [accountSidInput, storeCredentialMutation]);

    const handleSaveApiKey = React.useCallback(() => {
        if (!credentialQuery.data) return;
        setErrorMessage(null);
        setSuccessMessage(null);
        storeCredentialMutation.mutate({
            accountSid: credentialQuery.data.accountSid,
            apiKeySid: apiKeySidInput,
            apiKeySecret: apiKeySecretInput,
        });
    }, [
        credentialQuery.data,
        apiKeySidInput,
        apiKeySecretInput,
        storeCredentialMutation,
    ]);

    const handleSaveTwimlApp = React.useCallback(() => {
        if (!credentialQuery.data) return;
        setErrorMessage(null);
        setSuccessMessage(null);
        storeCredentialMutation.mutate({
            accountSid: credentialQuery.data.accountSid,
            voiceAppSid: voiceAppSidInput,
        });
    }, [credentialQuery.data, voiceAppSidInput, storeCredentialMutation]);

    const handleStartAccountSidEdit = React.useCallback(() => {
        setShowAccountSidForm(true);
        setAccountSidInput(credentialQuery.data?.accountSid ?? "");
        setErrorMessage(null);
        setSuccessMessage(null);
    }, [credentialQuery.data?.accountSid]);

    const handleStartApiKeyEdit = React.useCallback(() => {
        setShowApiKeyForm(true);
        setApiKeySidInput(credentialQuery.data?.apiKeySid ?? "");
        setApiKeySecretInput("");
        setErrorMessage(null);
        setSuccessMessage(null);
    }, [credentialQuery.data?.apiKeySid]);

    const handleStartTwimlAppEdit = React.useCallback(() => {
        setShowTwimlAppForm(true);
        setVoiceAppSidInput(credentialQuery.data?.voiceAppSid ?? "");
        setErrorMessage(null);
        setSuccessMessage(null);
    }, [credentialQuery.data?.voiceAppSid]);

    const handleCancelForm = React.useCallback(() => {
        setShowAccountSidForm(false);
        setShowApiKeyForm(false);
        setShowTwimlAppForm(false);
        setAccountSidInput("");
        setApiKeySidInput("");
        setApiKeySecretInput("");
        setVoiceAppSidInput("");
        setErrorMessage(null);
    }, []);

    const credential = credentialQuery.data;

    const hasApiKey = credential?.apiKeySid && credential?.hasApiKeySecret;
    const hasTwimlApp = Boolean(credential?.voiceAppSid);

    return (
        <ContentFull>
            <ContentHeader>Twilio</ContentHeader>
            {isFeatureEnabled ? (
                <div className="space-y-4">
                    <div>
                        Connect your Twilio account to enable SMS and voice call
                        functionality in {productName}. You will need your Account SID,
                        API Key credentials, and TwiML App SID from your Twilio console.
                    </div>

                    {successMessage && (
                        <InlineBanner type="success">{successMessage}</InlineBanner>
                    )}
                    {errorMessage && (
                        <InlineBanner type="error">{errorMessage}</InlineBanner>
                    )}

                    {!credential && !showAccountSidForm && (
                        <PrimaryButton onClick={() => setShowAccountSidForm(true)}>
                            Connect Twilio
                        </PrimaryButton>
                    )}

                    {!credential && showAccountSidForm && (
                        <div className="p-4 border border-gray-200 rounded space-y-4">
                            <h3 className="font-semibold text-lg">
                                Step 1: Enter Account SID
                            </h3>
                            <p className="text-sm text-gray-600">
                                Find your Account SID in your Twilio Console Dashboard.
                            </p>
                            <Input
                                label="Account SID"
                                value={accountSidInput}
                                onChange={e => setAccountSidInput(e.target.value)}
                                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                            />
                            <div className="flex justify-end space-x-2">
                                <SecondaryButton onClick={handleCancelForm}>
                                    Cancel
                                </SecondaryButton>
                                <PrimaryButton
                                    onClick={handleSaveAccountSid}
                                    disabled={
                                        !accountSidInput ||
                                        storeCredentialMutation.isPending
                                    }>
                                    {storeCredentialMutation.isPending && <Spinner />}
                                    <span
                                        className={
                                            storeCredentialMutation.isPending
                                                ? "ml-2"
                                                : ""
                                        }>
                                        Save
                                    </span>
                                </PrimaryButton>
                            </div>
                        </div>
                    )}

                    {credential && (
                        <div className="space-y-4">
                            <div className="p-3 border border-gray-100 rounded space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium">Account SID</h4>
                                        <p className="text-sm text-gray-600">
                                            {credential.accountSid}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            Connected{" "}
                                            {formatDate(new Date(credential.dateAdded))}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <StatusIndicator
                                            isComplete={true}
                                            label="Connected"
                                        />
                                        {!showAccountSidForm && (
                                            <SecondaryButton
                                                onClick={handleStartAccountSidEdit}>
                                                Update
                                            </SecondaryButton>
                                        )}
                                    </div>
                                </div>
                                {showAccountSidForm && (
                                    <div className="pt-3 border-t border-gray-200 space-y-4">
                                        <Input
                                            label="Account SID"
                                            value={accountSidInput}
                                            onChange={e =>
                                                setAccountSidInput(e.target.value)
                                            }
                                            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                        />
                                        <div className="flex justify-end space-x-2">
                                            <SecondaryButton onClick={handleCancelForm}>
                                                Cancel
                                            </SecondaryButton>
                                            <PrimaryButton
                                                onClick={handleSaveAccountSid}
                                                disabled={
                                                    !accountSidInput ||
                                                    storeCredentialMutation.isPending
                                                }>
                                                {storeCredentialMutation.isPending && (
                                                    <Spinner />
                                                )}
                                                <span
                                                    className={
                                                        storeCredentialMutation.isPending
                                                            ? "ml-2"
                                                            : ""
                                                    }>
                                                    Save
                                                </span>
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-3 border border-gray-100 rounded space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium">API Key</h4>
                                        {hasApiKey ? (
                                            <p className="text-sm text-gray-600">
                                                API Key SID: {credential.apiKeySid}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-gray-600">
                                                Add your API Key credentials for SMS and
                                                voice features
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <StatusIndicator
                                            isComplete={Boolean(hasApiKey)}
                                            label="Configured"
                                        />
                                        {!showApiKeyForm && (
                                            <SecondaryButton
                                                onClick={handleStartApiKeyEdit}>
                                                {hasApiKey ? "Update" : "Configure"}
                                            </SecondaryButton>
                                        )}
                                    </div>
                                </div>
                                {showApiKeyForm && (
                                    <div className="pt-3 border-t border-gray-200 space-y-4">
                                        <p className="text-sm text-gray-600">
                                            Create an API Key in your Twilio Console under
                                            Account
                                            {" > "}API Keys & Tokens. You&apos;ll need
                                            both the SID and Secret.
                                        </p>
                                        <Input
                                            label="API Key SID"
                                            value={apiKeySidInput}
                                            onChange={e =>
                                                setApiKeySidInput(e.target.value)
                                            }
                                            placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                        />
                                        <Input
                                            label="API Key Secret"
                                            type="password"
                                            value={apiKeySecretInput}
                                            onChange={e =>
                                                setApiKeySecretInput(e.target.value)
                                            }
                                            placeholder="Enter your API Key Secret"
                                        />
                                        <div className="flex justify-end space-x-2">
                                            <SecondaryButton onClick={handleCancelForm}>
                                                Cancel
                                            </SecondaryButton>
                                            <PrimaryButton
                                                onClick={handleSaveApiKey}
                                                disabled={
                                                    !apiKeySidInput ||
                                                    !apiKeySecretInput ||
                                                    storeCredentialMutation.isPending
                                                }>
                                                {storeCredentialMutation.isPending && (
                                                    <Spinner />
                                                )}
                                                <span
                                                    className={
                                                        storeCredentialMutation.isPending
                                                            ? "ml-2"
                                                            : ""
                                                    }>
                                                    Save
                                                </span>
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-3 border border-gray-100 rounded space-y-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium">TwiML App</h4>
                                        {hasTwimlApp ? (
                                            <p className="text-sm text-gray-600">
                                                TwiML App SID: {credential.voiceAppSid}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-gray-600">
                                                Add your TwiML App SID for voice call
                                                functionality
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <StatusIndicator
                                            isComplete={hasTwimlApp}
                                            label="Configured"
                                        />
                                        {!showTwimlAppForm && (
                                            <SecondaryButton
                                                onClick={handleStartTwimlAppEdit}>
                                                {hasTwimlApp ? "Update" : "Configure"}
                                            </SecondaryButton>
                                        )}
                                    </div>
                                </div>
                                {showTwimlAppForm && (
                                    <div className="pt-3 border-t border-gray-200 space-y-4">
                                        <p className="text-sm text-gray-600">
                                            Create a TwiML App in your Twilio Console
                                            under Develop {" > "} Voice {" > "} TwiML
                                            Apps.
                                        </p>
                                        <Input
                                            label="TwiML App SID"
                                            value={voiceAppSidInput}
                                            onChange={e =>
                                                setVoiceAppSidInput(e.target.value)
                                            }
                                            placeholder="APxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                        />
                                        <div className="flex justify-end space-x-2">
                                            <SecondaryButton onClick={handleCancelForm}>
                                                Cancel
                                            </SecondaryButton>
                                            <PrimaryButton
                                                onClick={handleSaveTwimlApp}
                                                disabled={
                                                    !voiceAppSidInput ||
                                                    storeCredentialMutation.isPending
                                                }>
                                                {storeCredentialMutation.isPending && (
                                                    <Spinner />
                                                )}
                                                <span
                                                    className={
                                                        storeCredentialMutation.isPending
                                                            ? "ml-2"
                                                            : ""
                                                    }>
                                                    Save
                                                </span>
                                            </PrimaryButton>
                                        </div>
                                    </div>
                                )}
                                {hasTwimlApp && (
                                    <div className="bg-gray-50 p-3 rounded space-y-4">
                                        <div className="space-y-2">
                                            <div className="text-sm font-medium">
                                                Voice Request URL
                                            </div>
                                            <CopyableLink
                                                url={`${window.location.origin}/api/twilio/voice/${credential.id}`}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-sm font-medium">
                                                SMS Request URL
                                            </div>
                                            <CopyableLink
                                                url={`${window.location.origin}/api/twilio/sms/${credential.id}`}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-sm font-medium">
                                                SMS Status Callback URL
                                            </div>
                                            <CopyableLink
                                                url={`${window.location.origin}/api/twilio/sms/status/${credential.id}`}
                                            />
                                        </div>
                                        <p className="text-xs text-gray-500">
                                            Configure these URLs in your TwiML App
                                            settings in the Twilio Console (Develop →
                                            Voice → TwiML Apps → [Your App]):
                                            <br />• Voice Request URL - for incoming calls
                                            <br />• SMS Request URL - for incoming text
                                            messages
                                            <br />• SMS Status Callback URL - for delivery
                                            status updates
                                        </p>
                                    </div>
                                )}
                            </div>

                            {!credential.isComplete && (
                                <InlineBanner type="warning">
                                    Complete all sections above to enable SMS and voice
                                    features.
                                </InlineBanner>
                            )}

                            <div className="pt-2">
                                <button
                                    onClick={() => handleDisconnect(credential.id)}
                                    className={linkStyle}>
                                    <span className="text-sm">Disconnect Twilio</span>
                                </button>
                            </div>

                            {credential.isComplete && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-medium">Phone Numbers</h3>
                                    {assignmentError && (
                                        <InlineBanner type="error">
                                            {assignmentError}
                                        </InlineBanner>
                                    )}
                                    {phoneNumbersQuery.isLoading ? (
                                        <div className="flex items-center space-x-2">
                                            <Spinner />
                                            <span>Loading phone numbers...</span>
                                        </div>
                                    ) : phoneNumbers.length > 0 ? (
                                        <CrmTable
                                            columns={phoneNumberColumns}
                                            data={phoneNumbers}
                                        />
                                    ) : (
                                        <InlineBanner type="info">
                                            No phone numbers found in your Twilio account.
                                        </InlineBanner>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <PremiumCallout>
                    <InlineBanner type="info">
                        Twilio is only available on request to {productName} Premium
                        customers. <br />
                        Please{" "}
                        <SupportEmailLink label="contact support" subject="Twilio" /> to
                        request an upgrade.
                    </InlineBanner>
                </PremiumCallout>
            )}
        </ContentFull>
    );
};
